/**
 * User-facing "edit vehicle" form.
 *
 * Route: /vehicles/:vehicleId/edit
 *
 * Editable fields: nickname / make / model / year / colour / PIN.
 * Licence plate is NOT editable here — it is changed from the detail
 * page ("Change licence plate"), which applies the change at once when
 * the plate is free and refuses a plate that is already registered.
 *
 * On success the shared useUserVehicles cache is refreshed so the
 * dashboard preview and My Vehicles list both reflect the change.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { LuCircleAlert, LuEye, LuEyeOff } from 'react-icons/lu';
import Page from '../../components/ui/Page/Page';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import Card from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import { SkeletonCard } from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import { useUserVehicles } from '../../hooks/useUserVehicles';
import { updateMyVehicle } from '../../api/userVehiclesApi';
import './VehicleEditPage.less';

const CURRENT_YEAR = new Date().getFullYear();

// The initial-form shape lives at module scope so both `useState` and
// the dirty-check compare against the same reference. `pin` starts
// blank by design (the server never returns the current PIN — a
// bcrypt hash isn't reversible).
const EMPTY_FORM = {
  displayName: '',
  make: '',
  model: '',
  year: '',
  color: '',
  pin: '',
};

function toInitialForm(vehicle) {
  if (!vehicle) return EMPTY_FORM;
  return {
    displayName: vehicle.displayName || '',
    make: vehicle.make || '',
    model: vehicle.model || '',
    year: vehicle.year != null ? String(vehicle.year) : '',
    color: vehicle.color || '',
    pin: '',
  };
}

function formsEqual(a, b) {
  return (
    a.displayName === b.displayName &&
    a.make === b.make &&
    a.model === b.model &&
    a.year === b.year &&
    a.color === b.color &&
    a.pin === b.pin
  );
}

export default function VehicleEditPage() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { vehicles, isLoading, refresh } = useUserVehicles();

  // The route param is plate-normalized (matches VehicleDetailPage) —
  // fall back to id in case a caller links by id.
  const vehicle = useMemo(
    () => vehicles.find((v) => v.plateNormalized === vehicleId || v.id === vehicleId),
    [vehicles, vehicleId],
  );

  const initialForm = useMemo(() => toInitialForm(vehicle), [vehicle]);

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPin, setShowPin] = useState(false);

  // Repopulate the form whenever the underlying vehicle changes
  // (initial load, a background refresh that swapped the cache).
  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  const cancelHref = `/vehicles/${encodeURIComponent(vehicleId)}`;

  const goBack = useCallback(() => {
    navigate(cancelHref);
  }, [navigate, cancelHref]);

  if (isLoading && !vehicle) {
    return (
      <Page>
        <PageHeader title="Edit vehicle" />
        <div className="gc-vehicle-edit-shell">
          <SkeletonCard />
        </div>
      </Page>
    );
  }

  if (!vehicle) {
    return (
      <Page>
        <PageHeader title="Vehicle not found" />
        <div className="gc-vehicle-edit-shell">
          <Card>
            <EmptyState
              icon={<LuCircleAlert />}
              title="We couldn't find this vehicle"
              description="It may no longer be on your account."
              action={<Button as={Link} to="/vehicles">Return to vehicles</Button>}
            />
          </Card>
        </div>
      </Page>
    );
  }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isDirty = !formsEqual(form, initialForm);

  const validate = () => {
    const e = {};
    if (form.year && form.year.trim() !== '') {
      const y = parseInt(form.year, 10);
      if (!Number.isFinite(y) || y < 1900 || y > CURRENT_YEAR + 2) {
        e.year = `Year must be between 1900 and ${CURRENT_YEAR + 2}.`;
      }
    }
    if (form.displayName && form.displayName.length > 80) e.displayName = 'Nickname is too long.';
    if (form.make && form.make.length > 80) e.make = 'Make is too long.';
    if (form.model && form.model.length > 80) e.model = 'Model is too long.';
    if (form.color && form.color.length > 40) e.color = 'Colour is too long.';
    if (form.pin && !/^\d{4}$/.test(form.pin)) {
      e.pin = 'PIN must be exactly 4 digits.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (evt) => {
    evt.preventDefault();
    // Guard against a double-tap that races through before saving flips.
    if (saving) return;
    if (!isDirty) return;
    setSubmitError(null);
    if (!validate()) return;
    setSaving(true);
    try {
      const patch = {
        vehicleName: form.displayName.trim() || null,
        make:  form.make.trim() || null,
        model: form.model.trim() || null,
        color: form.color.trim() || null,
      };
      if (form.year === '' || form.year == null) {
        patch.year = null;
      } else {
        patch.year = parseInt(form.year, 10);
      }
      // PIN is only sent when the user actually typed a new one. A
      // blank field must NEVER hit the wire — the server preserves
      // the existing PIN when the key is absent.
      const pinCandidate = (form.pin || '').trim();
      if (pinCandidate.length > 0) {
        patch.pin = pinCandidate;
      }
      await updateMyVehicle(vehicle.id, patch);
      await refresh();
      navigate(cancelHref);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Could not save changes.';
      setSubmitError(msg);
    } finally {
      setSaving(false);
    }
  };

  const displayPlate = vehicle.licensePlate || vehicle.plateNormalized;
  const saveDisabled = saving || !isDirty;

  return (
    <Page>
      {/* PageHeader has no action here — the redundant top-right
          Cancel was removed in favour of the bottom-right footer's
          single obvious cancel path. */}
      <PageHeader title="Edit vehicle" subtitle={displayPlate} />

      <div className="gc-vehicle-edit-shell">
        <Card className="gc-vehicle-edit-card">
          <header className="gc-vehicle-edit-header">
            <h2 className="gc-vehicle-edit-title">Vehicle details</h2>
            <p className="gc-vehicle-edit-sub">
              Update how your vehicle appears on receipts and in the app.
            </p>
          </header>

          <form onSubmit={submit} className="gc-vehicle-edit-form" noValidate>
            {/* Row 1: nickname (full width) */}
            <label className="gc-field gc-vehicle-edit-row-full">
              <span className="gc-field-label">Nickname</span>
              <input
                className="gc-field-input"
                type="text"
                value={form.displayName}
                onChange={(e) => setField('displayName', e.target.value)}
                placeholder="e.g. My BMW"
                maxLength={80}
                disabled={saving}
              />
              {errors.displayName && <span className="gc-field-error">{errors.displayName}</span>}
            </label>

            {/* Row 2: Make / Model */}
            <div className="gc-vehicle-edit-row">
              <label className="gc-field">
                <span className="gc-field-label">Make</span>
                <input
                  className="gc-field-input"
                  type="text"
                  value={form.make}
                  onChange={(e) => setField('make', e.target.value)}
                  placeholder="e.g. BMW"
                  maxLength={80}
                  disabled={saving}
                />
                {errors.make && <span className="gc-field-error">{errors.make}</span>}
              </label>

              <label className="gc-field">
                <span className="gc-field-label">Model</span>
                <input
                  className="gc-field-input"
                  type="text"
                  value={form.model}
                  onChange={(e) => setField('model', e.target.value)}
                  placeholder="e.g. 116i"
                  maxLength={80}
                  disabled={saving}
                />
                {errors.model && <span className="gc-field-error">{errors.model}</span>}
              </label>
            </div>

            {/* Row 3: Year / Colour */}
            <div className="gc-vehicle-edit-row">
              <label className="gc-field">
                <span className="gc-field-label">Year</span>
                <input
                  className="gc-field-input"
                  type="number"
                  inputMode="numeric"
                  min={1900}
                  max={CURRENT_YEAR + 2}
                  value={form.year}
                  onChange={(e) => setField('year', e.target.value)}
                  placeholder="e.g. 2020"
                  disabled={saving}
                />
                {errors.year && <span className="gc-field-error">{errors.year}</span>}
              </label>

              <label className="gc-field">
                <span className="gc-field-label">Colour</span>
                <input
                  className="gc-field-input"
                  type="text"
                  value={form.color}
                  onChange={(e) => setField('color', e.target.value)}
                  placeholder="e.g. Silver"
                  maxLength={40}
                  disabled={saving}
                />
                {errors.color && <span className="gc-field-error">{errors.color}</span>}
              </label>
            </div>

            {/* Row 4: PIN with show/hide toggle */}
            <label className="gc-field gc-vehicle-edit-row-full">
              <span className="gc-field-label">New vehicle PIN</span>
              <div className="gc-vehicle-edit-pin">
                <input
                  className="gc-field-input"
                  type={showPin ? 'text' : 'password'}
                  inputMode="numeric"
                  autoComplete="new-password"
                  maxLength={4}
                  pattern="[0-9]{4}"
                  value={form.pin}
                  onChange={(e) => setField('pin', e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="••••"
                  disabled={saving}
                  aria-describedby="gc-vehicle-pin-help"
                />
                <button
                  type="button"
                  className="gc-vehicle-edit-pin-toggle"
                  onClick={() => setShowPin((s) => !s)}
                  aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                  aria-pressed={showPin}
                  disabled={saving}
                >
                  {showPin ? <LuEyeOff aria-hidden="true" /> : <LuEye aria-hidden="true" />}
                </button>
              </div>
              <span id="gc-vehicle-pin-help" className="gc-field-hint">
                Leave the PIN blank to keep your current PIN.
              </span>
              {errors.pin && <span className="gc-field-error">{errors.pin}</span>}
            </label>

            <p className="gc-vehicle-edit-note">
              To change the licence plate, use “Change licence plate” on the vehicle page.
            </p>

            {submitError && (
              <div className="gc-form-error" role="alert">
                {submitError}
              </div>
            )}

            <div className="gc-vehicle-edit-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={goBack}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                busy={saving}
                disabled={saveDisabled}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Page>
  );
}
